import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import FilterToolbar from '../../components/FilterToolbar';
import { getOpeningStocks, getStores } from '../../api/stockLedger';
import { formatDate } from '../../utils';
import { storeDisplayName } from '../../utils/storeEntity';
import useInvRole from '../../hooks/useInvRole';
import useAssignedStoreOptions from '../../hooks/useAssignedStoreOptions';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

export default function OpeningStockList() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const stockBase = url.replace('/opening-stocks', '');
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const {
    storeLocked, activeStoreId, setActiveStoreId, isStoreScoped, storeOptions,
  } = useAssignedStoreOptions(stores, { allLabel: 'All Stores' });
  const [storeFilter, setStoreFilter] = useState(activeStoreId || '');

  useEffect(() => {
    // API: GET /inventory/stores/
    getStores().then(d => setStores(normalizeList(d))).catch(() => {});
  }, []);

  useEffect(() => {
    if (isStoreScoped && activeStoreId) setStoreFilter(activeStoreId);
  }, [isStoreScoped, activeStoreId]);

  const resolveStoreName = useCallback((storeId) => {
    if (!storeId) return '—';
    const store = stores.find(s => (s.store_id || s.id) === storeId);
    return store ? storeDisplayName(store) : storeId;
  }, [stores]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = {};
      if (storeFilter) p.store_id = storeFilter;
      // API: GET /inventory/stock/opening-stock/
      const data = await getOpeningStocks(p);
      setItems(normalizeList(data));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [storeFilter]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    { field: 'opening_date', headerName: 'Date', flex: 0.7, minWidth: 120, valueFormatter: p => formatDate(p.value) },
    { field: 'store_id', headerName: 'Store', flex: 1.2, minWidth: 180,
      valueGetter: p => resolveStoreName(p.row.store_id) },
    { field: 'status', headerName: 'Status', flex: 0.7, minWidth: 120,
      renderCell: p => (p.value ? <StatusChip status={p.value} /> : null) },
    { field: 'remarks', headerName: 'Remarks', flex: 1.4, minWidth: 180,
      renderCell: p => (
        <Typography noWrap sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>
          {p.value || '—'}
        </Typography>
      ) },
    { field: 'actions', headerName: '', flex: 0.4, minWidth: 80, sortable: false,
      renderCell: p => (
        <Tooltip title="View">
          <IconButton size="small" onClick={() => history.push(`${url}/${p.row.id}`)}
            sx={{ color: IPD_COLORS.primary }}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) },
  ], [history, resolveStoreName, url]);

  const lockedStoreLabel = storeOptions.find(o => o.value === storeFilter)?.label || '';

  return (
    <Box>
      <PageHeader eyebrow="Inventory Management" title="Opening Stock" description={`${items.length} entries`}
        actions={can(INV_ACTIONS.OPENING_STOCK_CREATE) ? (
          <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${stockBase}/opening-stock/new`)}>
            New Opening Stock
          </InvButton>
        ) : null}
      />
      <FilterToolbar>
        {storeLocked ? (
          <FilterToolbar.StaticValue label="Store" width={280} value={lockedStoreLabel} />
        ) : (
          <>
            <FilterToolbar.Filter
              label="Store"
              width={280}
              value={storeFilter}
              onChange={e => {
                setStoreFilter(e.target.value);
                if (isStoreScoped) setActiveStoreId(e.target.value);
              }}
              options={storeOptions}
            />
            {storeFilter && <FilterToolbar.Reset onClick={() => setStoreFilter('')} />}
          </>
        )}
      </FilterToolbar>
      <InvDataGrid fillHeight rows={items} columns={columns} loading={loading} error={error} onRetry={load}
        getRowId={r => r.id} emptyMessage="No opening stock entries found."
        emptyAction={can(INV_ACTIONS.OPENING_STOCK_CREATE)
          ? <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${stockBase}/opening-stock/new`)}>Create Opening Stock</InvButton>
          : null} />
    </Box>
  );
}
